import { SegmentedTabs } from '@skupreme/shared/ui';
import { ToastMessage } from '@utility/toast';
import { useSearchParams } from 'react-router';
import { useUnmapListing } from '../../hooks/use-unmap-listing';
import { mappingErrorMessage } from '../../lib/mapping-errors';
import { MappableListingsTable } from '../MappableListingsTable';
import { MappedProductsTable } from './MappedProductsTable';
import { MappedSummary } from './MappedSummary';

const noop = () => {};

/* M2: the Mapped view lists listings, or groups them by product (`?group=product`). */
export const PRODUCT_GROUP = 'product';
const LISTING_GROUP = 'listing';
const GROUP_TABS = [
  { id: LISTING_GROUP, label: 'Listings' },
  { id: PRODUCT_GROUP, label: 'Products' },
];

export type MappedViewProps = {
  isReadOnly?: boolean;
  isActionsHidden?: boolean;
};

/** Mapped listings under their summary, with Undo (back to the queue) in each row's menu. */
export function MappedView({ isReadOnly, isActionsHidden }: MappedViewProps) {
  const [params, setParams] = useSearchParams();
  const group =
    params.get('group') === PRODUCT_GROUP ? PRODUCT_GROUP : LISTING_GROUP;
  const unmap = useUnmapListing();

  const undo = async (listingId: string) => {
    try {
      await unmap.mutateAsync(listingId);
      ToastMessage.success('Listing moved back to To review');
    } catch (failure) {
      ToastMessage.error(mappingErrorMessage(failure));
    }
  };

  return (
    <div className="sk:flex sk:flex-col sk:gap-3">
      <MappedSummary />
      <SegmentedTabs
        label="Group mapped listings"
        className="sk:self-start"
        items={GROUP_TABS}
        selectedKey={group}
        onSelectionChange={key =>
          setParams(
            key === PRODUCT_GROUP
              ? { view: 'mapped', group: PRODUCT_GROUP }
              : { view: 'mapped' },
          )
        }
      />
      {group === PRODUCT_GROUP ? (
        <MappedProductsTable
          isReadOnly={isReadOnly}
          isActionsHidden={isActionsHidden}
          onUndo={undo}
          isUndoing={unmap.isPending}
        />
      ) : (
        <MappableListingsTable
          state="mapped"
          onStateChange={noop}
          hideStateTabs
          isReadOnly={isReadOnly}
          isActionsHidden={isActionsHidden}
          onUndo={listing => undo(listing.id)}
          isUndoing={unmap.isPending}
        />
      )}
    </div>
  );
}
